/**
 * Validates if a redirect URL is safe.
 *
 * A URL is considered safe if:
 * - It's a relative path (starts with '/')
 * - It's an absolute path whose hostname is exactly the rootDomain or ends with '.{rootDomain}'
 * - It uses http or https protocol.
 *
 * @param redirectUrl The URL to validate.
 * @param rootDomain The allowed root domain (e.g., ".getresq.com").
 * @returns True if the URL is safe, false otherwise.
 */
export const validateRedirectUrl = (redirectUrl: string, rootDomain: string): boolean => {
    if (!redirectUrl || !rootDomain) {
        return false;
    }

    // Allow relative URLs starting with /
    if (redirectUrl.startsWith('/') && !redirectUrl.startsWith('//')) {
        return true;
    }

    try {
        const url = new URL(redirectUrl);

        // Check protocol
        if (!['http:', 'https:'].includes(url.protocol)) {
            return false;
        }

        // Check hostname against rootDomain (allow exact match or subdomain)
        const hostname = url.hostname;
        // Ensure rootDomain starts with a dot for subdomain checks unless it's an exact match
        const domainPattern = rootDomain.startsWith('.') ? rootDomain : `.${rootDomain}`;

        if (hostname === rootDomain.replace(/^\./, '') || hostname.endsWith(domainPattern)) {
            return true;
        }
    } catch (e) {
        // Invalid URL format
        console.error('Error parsing redirect URL:', e);
        return false;
    }

    return false;
};

/**
 * Validates a custom-scheme (mobile deep link) redirect URI against an allowlist.
 *
 * Example allowed schemes: ["resq-facility://", "resq-partner://"]
 */
export const validateCustomSchemeRedirectUri = (
    redirectUri: string,
    allowedSchemes: string[],
): boolean => {
    if (!redirectUri || allowedSchemes.length === 0) {
        return false;
    }
    return allowedSchemes.some((scheme) => redirectUri.startsWith(scheme));
};
